/**
 * rbac.ts — Role-Based Access Control + Feature-Flag helpers
 *
 * ─── B2B Workspace model ────────────────────────────────────────────────────
 *
 * Three orthogonal axes of access control:
 *
 *  1. Plan tier  (profiles.plan_tier)
 *     Governs which features a user can access across all workspaces.
 *       free / premium / admin
 *
 *  2. Company module licence  (companies.active_modules TEXT[])
 *     A module is VISIBLE only if the currently active company has it
 *     in its active_modules array.  This is a company-level gate.
 *     ⚠️  Super-admins are NOT exempt from this check for SIDEBAR visibility
 *         — they see exactly what clients see.  They ARE exempt from ROUTE
 *         guards via can(), so they can still navigate everywhere.
 *
 *  3. User-level permissions  (company_members.module_permissions)
 *     Governs what the user can DO inside an already-unlocked module.
 *       role: 'admin' | 'member'
 *       levels: { cashflow?: viewer|editor|admin; hr?: viewer|editor|admin }
 *
 * ─── Two distinct helper functions ─────────────────────────────────────────
 *
 *  companyModuleActive(activeModules, module)
 *    → Pure data check: is this module in the company's active_modules array?
 *    → NO super-admin bypass.  Use for sidebar / UI visibility.
 *
 *  can(permissions, request)
 *    → Full access-decision function for ROUTE guards and write operations.
 *    → Super-admin bypasses everything.
 */

import type { PlanTier, ModulePermissions, AppModule } from '@/types/database';

// ─── Re-exports ───────────────────────────────────────────────────────────────
export type { AppModule };

// ─── Access level ─────────────────────────────────────────────────────────────
export type AccessLevel = 'admin' | 'editor' | 'viewer' | 'none';

// ─── Permission request ───────────────────────────────────────────────────────
export interface PermissionRequest {
  module?:              AppModule | 'reconciliation' | 'advanced_reports';
  level?:               AccessLevel;
  minPlan?:             PlanTier;
  requireCompanyAdmin?: boolean;
}

// ─── User permission snapshot ─────────────────────────────────────────────────
export interface UserPermissions {
  planTier:          PlanTier;
  companyRole:       'admin' | 'member' | null;
  modulePermissions: ModulePermissions | null;
  isSuperAdmin:      boolean;
  /** Raw list from companies.active_modules — empty = nothing unlocked. */
  activeModules:     AppModule[];
}

// ─── Plan tier ordering ───────────────────────────────────────────────────────
const PLAN_ORDER: Record<PlanTier, number> = { free: 0, premium: 1, admin: 2 };

export function meetsPlanTier(actual: PlanTier, required: PlanTier): boolean {
  return PLAN_ORDER[actual] >= PLAN_ORDER[required];
}

// ─── Access level ordering ────────────────────────────────────────────────────
const LEVEL_ORDER: Record<AccessLevel, number> = {
  none: 0, viewer: 1, editor: 2, admin: 3,
};

export function meetsAccessLevel(actual: AccessLevel, required: AccessLevel): boolean {
  return LEVEL_ORDER[actual] >= LEVEL_ORDER[required];
}

// ─── Module → user-permission key mapping ────────────────────────────────────
type MappedModule = keyof ModulePermissions;   // 'cashflow' | 'hr' | 'inventory' | 'invoice_intake' | 'interfaces'

const MODULE_TO_PERM_KEY: Partial<Record<string, MappedModule>> = {
  cashflow:        'cashflow',
  hr:              'hr',
  inventory:       'inventory',
  invoice_intake:  'invoice_intake',
  interfaces:      'interfaces',
};

// ─── ★ Company module active check (NO super-admin bypass) ───────────────────
/**
 * Pure company-data check: is this module in the company's active_modules array?
 *
 * ⚠️  Intentionally does NOT grant super-admins a pass.
 *     Super-admins see what the company sees in the sidebar.
 *
 * Use this for:
 *   • Sidebar nav item visibility (moduleGate)
 *   • Any "what does this company have?" UI query
 *
 * Do NOT use for route guards — use `can()` for that.
 */
export function companyModuleActive(
  activeModules: AppModule[],
  module: string,
): boolean {
  return activeModules.includes(module as AppModule);
}

// ─── Legacy alias (kept for any callers that use companyHasModule) ────────────
/**
 * @deprecated  Use companyModuleActive(activeModules, module) for sidebar checks.
 *              This wrapper still grants super-admin a bypass — do not use for UI visibility.
 */
export function companyHasModule(
  perms: UserPermissions,
  module: string,
): boolean {
  if (perms.isSuperAdmin) return true;
  return companyModuleActive(perms.activeModules, module);
}

// ─── Core permission check (ROUTE guards & write operations) ─────────────────
/**
 * Central access-decision function.  Returns true if the user satisfies ALL constraints.
 *
 * Rules (first failure short-circuits):
 *  1. Super-admin → always true (bypasses everything including module gate).
 *  2. Plan tier must meet minPlan.
 *  3. requireCompanyAdmin → companyRole must be 'admin'.
 *  4. If module specified:
 *     a. Company licence gate: active_modules must include the module.
 *        (non-AppModule values like 'advanced_reports' skip this gate.)
 *     b. Per-user level check (company admins always pass this step).
 */
export function can(
  perms: UserPermissions,
  req:   PermissionRequest = {},
): boolean {
  const {
    module,
    level               = 'viewer',
    minPlan             = 'free',
    requireCompanyAdmin = false,
  } = req;

  // 1. Super-admin bypasses ALL route guards
  if (perms.isSuperAdmin) return true;

  // 2. Plan tier
  if (!meetsPlanTier(perms.planTier, minPlan)) return false;

  // 3. Company admin requirement
  if (requireCompanyAdmin && perms.companyRole !== 'admin') return false;

  // 4. Module checks
  if (module) {
    const permKey = MODULE_TO_PERM_KEY[module];
    if (permKey) {
      // 4a. Company licence gate (uses raw activeModules, no SA bypass here
      //     because SA was already returned true at step 1)
      if (!companyModuleActive(perms.activeModules, module)) return false;

      // 4b. Per-user level — company admins always win
      if (perms.companyRole === 'admin') return true;

      const storedLevel: AccessLevel =
        (perms.modulePermissions?.[permKey] as AccessLevel | undefined) ?? 'none';
      return meetsAccessLevel(storedLevel, level);
    }
    // Module not in per-user perm key map (e.g. 'advanced_reports').
    // Still enforce the company licence gate if it is a known AppModule.
    const isAppModule = (['cashflow', 'hr', 'inventory', 'invoice_intake', 'interfaces'] as string[]).includes(module as string);
    if (isAppModule && !companyModuleActive(perms.activeModules, module)) return false;
  }

  return true;
}

// ─── Convenience helpers ──────────────────────────────────────────────────────
export const canViewAny       = (p: UserPermissions) => can(p);
export const canAccessPremium = (p: UserPermissions) => can(p, { minPlan: 'premium' });
export const isCompanyAdmin   = (p: UserPermissions) =>
  p.isSuperAdmin || p.companyRole === 'admin';
